import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios';
import { sharePointService } from '../services/sharePointService';
import { getChecklist } from '../config/documentChecklists';
import { ELEMENT_LABELS } from '../config/elementLabels';
import { Modal, Button } from '../components';
import '../styles/OwnershipDocumentation.scss';

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const DocumentationPage = (props) => {
  const navigate = useNavigate();
  const { elementKey: paramKey } = useParams();
  const elementKey = props.elementKey || paramKey;
  const folderName = ELEMENT_LABELS[elementKey] || elementKey;

  // --- existing (non-SharePoint) doc-type-grid upload, unchanged behavior ---
  const fileInputRef = useRef(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [uploads, setUploads] = useState({});
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState([]);


  const getElementFolders = async (element) => {
    let items = [];
    try {
      const res = await api.get(`core/sharepoint/element-folders/`, { params: { element } });
      items = res.data.map((doc) => doc.name);
    } catch (err) {
      items = [];
    }

    if (items.length === 0) {
      items = getChecklist(elementKey);
    }

    setDocs(items);
  }

  const handleClick = (doc) => {
    setActiveDoc(doc);
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleUpload = async (files) => {
    if (!files.length || !activeDoc) return;
    setUploading(true);

    // Graph API requirement: chunk size must be a multiple of 327,680 bytes (320 KiB)
    const CHUNK_SIZE = 320 * 1024 * 10; // 3.2 MB per chunk

    try {
      const uploaded = [];
      for (const file of files) {
        const res = await api.get(`core/sharepoint/upload-url/`, {
          params: { element: elementKey, folder: ELEMENT_LABELS[elementKey], filename: file.name },
        });
        const url = res.data.uploadUrl;

        const totalSize = file.size;
        let start = 0;

        while (start < totalSize) {
          const end = Math.min(start + CHUNK_SIZE, totalSize);
          const chunk = file.slice(start, end);

          // Define the headers required by Microsoft Graph for an upload session range
          const headers = {
            'Content-Length': end - start,
            'Content-Range': `bytes ${start}-${end - 1}/${totalSize}`,
          };

          // Make the PUT request with the raw binary chunk payload
          const response = await axios.put(url, chunk, { headers });

          // Graph returns 200 or 201 when the final chunk finishes the entire upload session
          if (response.status === 200 || response.status === 201) {
            break;
          }

          // Prepare the next chunk slice position
          start = end;
        }
        uploaded.push(file.name);
      }

      setUploads((prev) => ({
        ...prev,
        [activeDoc]: [...(prev[activeDoc] || []), ...uploaded],
      }));
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    api.get(`/core/documents/?element=${elementKey}`)
      .then((res) => {
        const existing = {};
        res.data.forEach((doc) => {
          existing[doc.doc_type] = [...(existing[doc.doc_type] || []), doc.filename];
        });
        setUploads(existing);
      })
      .catch(() => {});
      // --- new sharepoint thing ----
      getElementFolders(elementKey);
  }, [elementKey]);

  const uploadedCount = docs.filter((doc) => uploads[doc]?.length > 0).length;
  const total = docs.length;
  const percentage = total ? Math.round((uploadedCount / total) * 100) : 0;
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // --- new SharePoint section ---
  const [currentFolder, setCurrentFolder] = useState('');
  const [spItems, setSpItems] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spError, setSpError] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const spFileInputRef = useRef(null);

  const loadSpFiles = useCallback(async (folder) => {
    setSpLoading(true);
    setSpError('');
    try {
      const data = await sharePointService.listFiles(folder, elementKey);
      setSpItems(data);
    } catch (err) {
      setSpError(err.response?.data?.error || 'Failed to load files');
    } finally {
      setSpLoading(false);
    }
  }, [elementKey]);

  useEffect(() => { loadSpFiles(currentFolder); }, [currentFolder, loadSpFiles]);

  const spBreadcrumbs = currentFolder ? currentFolder.split('/') : [];

  const handleSpUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await sharePointService.uploadFile(file, currentFolder, elementKey);
      await loadSpFiles(currentFolder);
    } catch (err) {
      setSpError(err.response?.data?.error || 'Upload failed');
    } finally {
      if (spFileInputRef.current) spFileInputRef.current.value = '';
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const folderPath = currentFolder ? `${currentFolder}/${newFolderName.trim()}` : newFolderName.trim();
    try {
      await sharePointService.createFolder(folderPath, elementKey);
      setShowNewFolder(false);
      setNewFolderName('');
      await loadSpFiles(currentFolder);
    } catch (err) {
      setSpError(err.response?.data?.error || 'Failed to create folder');
    }
  };

  const handleSpDownload = async (filename) => {
    try {
      await sharePointService.downloadFile(currentFolder, filename, elementKey);
    } catch (err) {
      setSpError(err.response?.data?.error || 'Download failed');
    }
  };

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1>Documentation</h1>
          <p>{folderName} — Required Documents &amp; Evidence</p>
        </div>
        <div className="od-progress-ring" title={`${uploadedCount} of ${total} documents uploaded`}>
          <svg width="138" height="138" viewBox="0 0 138 138">
            <circle className="od-progress-ring__track" cx="69" cy="69" r={radius} fill="none" strokeWidth="15" />
            <circle
              className="od-progress-ring__fill"
              cx="69" cy="69" r={radius} fill="none" strokeWidth="15"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              transform="rotate(-90 69 69)"
            />
          </svg>
          <div className="od-progress-ring__label">
            <span className="od-progress-ring__pct">{percentage}%</span>
            <span className="od-progress-ring__sub">{uploadedCount}/{total}</span>
          </div>
        </div>
      </div>

      <div className="od-section-label">Select a document to upload</div>
      <div className="od-grid">
        {docs.map((doc) => (
          <button
            key={doc}
            className={`od-card ${uploads[doc]?.length ? 'od-card--uploaded' : ''}`}
            onClick={() => handleClick(doc)}
            disabled={uploading}
          >
            <div className="od-card-icon"><UploadIcon /></div>
            <span className="od-card-label">{doc}</span>
            {uploads[doc]?.length > 0 && (
              <span className="od-card-status">
                {uploads[doc].map((name, i) => <span key={i}>✓ {name}</span>)}
              </span>
            )}
          </button>
        ))}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.pdf,.docx,.jpg,.png"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleUpload(Array.from(e.target.files))}
      />

      <hr style={{ margin: '32px 0' }} />

      <div className="od-section-label">Upload to SharePoint</div>
      <div className="breadcrumbs">
        <span onClick={() => setCurrentFolder('')}>{folderName}</span>
        {spBreadcrumbs.map((seg, i) => (
          <span key={i} onClick={() => setCurrentFolder(spBreadcrumbs.slice(0, i + 1).join('/'))}> / {seg}</span>
        ))}
      </div>

      <div className="toolbar" style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
        <input ref={spFileInputRef} type="file" onChange={handleSpUpload} />
        <Button onClick={() => setShowNewFolder(true)}>New Folder</Button>
      </div>

      {spError && <div className="error">{spError}</div>}
      {spLoading ? (
        <p>Loading...</p>
      ) : (
        <ul className="file-list">
          {spItems.map((item) => (
            <li key={item.id}>
              {item.is_folder ? (
                <span onClick={() => setCurrentFolder(currentFolder ? `${currentFolder}/${item.name}` : item.name)}>
                  📁 {item.name}
                </span>
              ) : (
                <span>
                  📄 {item.name} ({item.size} bytes)
                  <button onClick={() => handleSpDownload(item.name)}>Download</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={showNewFolder}
        onClose={() => setShowNewFolder(false)}
        title="New Folder"
        footer={<Button onClick={handleCreateFolder}>Create</Button>}
      >
        <input
          type="text"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder="Folder name"
        />
      </Modal>
    </div>
  );
};

export default DocumentationPage;
